import { Caption } from '@moneyforward/ap-frontend-components';
import { useEffect, useMemo, useRef, type ReactNode } from 'react';
import {
  useFormContext,
  type FieldPath,
  type FieldValues,
  type RegisterOptions,
} from 'react-hook-form';

const isEmptyValue = (value: unknown): boolean =>
  value === undefined ||
  value === null ||
  value === '' ||
  (Array.isArray(value) && value.length === 0);

export const MISMATCH_RULE_NAME = 'mismatch';

export type MismatchCaptionRender = (
  type: 'warning' | 'error',
  ruleName: string,
  children: ReactNode
) => ReactNode | undefined;

/**
 * Library `captionRender` for the OCR mismatch warning. `undefined` leaves
 * the default caption in place (required errors, 13-digit, NTA, …).
 *
 * Due date reports `ruleName === 'mismatch'`. Registration overwrites
 * `warningOrError` with `input13Text`, so that path is recognized by the
 * message text instead.
 */
export const createMismatchCaptionRender =
  (mismatchMessage?: string): MismatchCaptionRender =>
  // Render prop for the library caption slot, not a React component.
  // eslint-disable-next-line react/display-name
  (type, ruleName, children) => {
    if (type !== 'warning') {
      return undefined;
    }
    const isMismatchRule = ruleName === MISMATCH_RULE_NAME;
    // The registration path only has the message text to go on (see above),
    // so this assumes the library keeps passing the raw string through as
    // `children` rather than wrapping it. If that ever changes, this simply
    // stops matching and falls back to the default caption below.
    const isMismatchMessage =
      typeof children === 'string' && children === mismatchMessage;
    if (!isMismatchRule && !isMismatchMessage) {
      return undefined;
    }
    return <Caption type='alert'>{children}</Caption>;
  };

export type UseMismatchWarningRulesResult<TFieldValues extends FieldValues> = {
  rules: RegisterOptions<TFieldValues>;
  captionRender: MismatchCaptionRender | undefined;
};

/**
 * Surfaces a mismatch message through the shared form warning state, so the
 * field renders the alert styling and caption instead of a hover tooltip.
 * Returning a string from `warningOrError` marks the field as a warning
 * (an object with `error: true` would mark it as an error instead).
 *
 * The required check runs inside `warningOrError` rather than through
 * `rules.required`: react-hook-form skips `validate` once a built-in rule
 * fails, which would leave the library's warning state holding a stale
 * message while the field is empty.
 *
 * `warningOrError` is part of the component library's RegisterWarningOptions,
 * which is not re-exported from its package root, hence the assertion.
 *
 * `captionRender` is the 0.0.219 custom-caption hook: pass it to the form
 * control so `ruleName` can distinguish the mismatch warning from other
 * captions. `undefined` from the caller keeps this default; `null` opts
 * out to the library caption; a function replaces it.
 */
export const useMismatchWarningRules = <TFieldValues extends FieldValues>(
  name: FieldPath<TFieldValues>,
  required: boolean,
  requiredMessage: string,
  mismatchMessage?: string,
  callerCaptionRender?: MismatchCaptionRender | null
): UseMismatchWarningRulesResult<TFieldValues> => {
  const formContext = useFormContext<TFieldValues>();
  if (!formContext) {
    throw new Error(
      'useMismatchWarningRules must be used within a FormProvider'
    );
  }
  const { trigger } = formContext;

  // Read through refs so the returned rules stay referentially stable; the
  // library memoizes on `rules`, and a new object on every validation would
  // re-render the field even when nothing changed.
  const latest = useRef({ required, requiredMessage, mismatchMessage });
  latest.current = { required, requiredMessage, mismatchMessage };
  const triggerRef = useRef(trigger);
  triggerRef.current = trigger;

  // The warning is held in validation state, so it only refreshes when
  // validation reruns. Edits already trigger that, but the mismatch arrives
  // asynchronously, so revalidate whenever it appears or clears. `trigger`
  // is kept out of the deps because validating changes form state, which
  // would otherwise loop back into this effect.
  //
  // Skip only when there has never been a mismatch (mount, and every render
  // before one first arrives): triggering then would run validate on an
  // untouched field and could surface a required error before the user has
  // interacted with it.
  const prevMismatchRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const prevMismatchMessage = prevMismatchRef.current;
    prevMismatchRef.current = mismatchMessage;
    if (!mismatchMessage && !prevMismatchMessage) return;
    triggerRef.current(name);
  }, [mismatchMessage, name]);

  const rules = useMemo(
    () =>
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      ({
        warningOrError: {
          // Split into two named rules (rather than one combined function)
          // so the library's `ruleName` (surfaced via useCaption) reports
          // which one actually fired. The library evaluates keys in
          // definition order and stops at the first error, so `required`
          // still takes priority over `mismatch` exactly as before.
          //
          // Required errors still report `ruleName: 'original'`: the
          // library turns `{ error: true, message }` into validate.original.
          required: (fieldValue: unknown) => {
            const current = latest.current;
            if (current.required && isEmptyValue(fieldValue)) {
              return { error: true, message: current.requiredMessage };
            }
            return true;
          },
          [MISMATCH_RULE_NAME]: () => latest.current.mismatchMessage ?? true,
        },
      } as RegisterOptions<TFieldValues>),
    []
  );

  const mismatchCaptionRender = useMemo(
    () => createMismatchCaptionRender(mismatchMessage),
    [mismatchMessage]
  );

  const captionRender =
    callerCaptionRender === undefined
      ? mismatchCaptionRender
      : callerCaptionRender === null
      ? undefined
      : callerCaptionRender;

  return { rules, captionRender };
};
